"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Client-side driver for a reCAPTCHA **v2 Invisible** widget.
 *
 * v2 Invisible has no UI of its own: you render a widget into a container, then
 * call `execute()` at submit time and wait for a token to come back through a
 * callback (Google may interpose a challenge first). That callback-based flow is
 * wrapped here as a promise so form handlers can just `await getToken()`.
 *
 * Reusable across forms — each hook instance renders its own widget, so the
 * quote / find-an-agent / newsletter forms can adopt it independently.
 */

interface RenderParams {
  sitekey: string;
  size: "invisible";
  badge?: "bottomright" | "bottomleft" | "inline";
  callback: (token: string) => void;
  "error-callback": () => void;
  "expired-callback": () => void;
}

interface GrecaptchaApi {
  ready(cb: () => void): void;
  render(container: HTMLElement, params: RenderParams): number;
  execute(widgetId: number): void;
  reset(widgetId: number): void;
}

declare global {
  interface Window {
    grecaptcha?: GrecaptchaApi;
  }
}

const SCRIPT_ID = "recaptcha-api-js";

// A challenge can sit on screen indefinitely, and dismissing it fires no
// callback at all — without this the submit button would stay stuck on
// "Sending..." forever.
const TOKEN_TIMEOUT_MS = 120_000;

// Shared across hook instances so multiple forms on one page load the script once.
let scriptPromise: Promise<void> | null = null;

function loadRecaptchaScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    if (window.grecaptcha) {
      resolve();
      return;
    }

    const fail = () => {
      // Let a later attempt retry rather than caching the failure forever.
      scriptPromise = null;
      reject(new Error("Failed to load the reCAPTCHA script"));
    };

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", fail);
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    // `render=explicit` — we place the widget ourselves via grecaptcha.render().
    script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = fail;
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export interface UseInvisibleRecaptcha {
  /** Attach to an empty element the widget mounts into. */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** True once the widget is rendered and `getToken()` can produce a token. */
  ready: boolean;
  /**
   * Run the challenge and resolve with a token.
   *
   * Resolves with `""` when reCAPTCHA is unavailable — no site key configured,
   * or the script was blocked (ad blockers and some corporate networks do block
   * it). The server decides what an empty token means, so behaviour stays in one
   * place: accepted while no secret is configured, rejected once there is one.
   */
  getToken: () => Promise<string>;
}

export function useInvisibleRecaptcha(): UseInvisibleRecaptcha {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<number | null>(null);
  const pendingRef = useRef<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);
  const [ready, setReady] = useState(false);

  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

  const settle = useCallback(
    (outcome: { token: string } | { error: Error }) => {
      const pending = pendingRef.current;
      if (!pending) return;
      pendingRef.current = null;
      clearTimeout(pending.timer);
      if ("token" in outcome) pending.resolve(outcome.token);
      else pending.reject(outcome.error);
    },
    [],
  );

  useEffect(() => {
    if (!siteKey || !containerRef.current) return;

    let cancelled = false;

    loadRecaptchaScript()
      .then(
        () =>
          new Promise<void>((resolve) => {
            window.grecaptcha?.ready(() => resolve());
          }),
      )
      .then(() => {
        if (
          cancelled ||
          !containerRef.current ||
          widgetIdRef.current !== null ||
          !window.grecaptcha
        ) {
          return;
        }

        widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
          sitekey: siteKey,
          size: "invisible",
          badge: "bottomright",
          callback: (token: string) => settle({ token }),
          "error-callback": () =>
            settle({ error: new Error("reCAPTCHA failed to verify") }),
          "expired-callback": () =>
            settle({ error: new Error("reCAPTCHA expired") }),
        });
        setReady(true);
      })
      .catch(() => {
        // Stay unready; getToken() then resolves "" and the server decides.
      });

    return () => {
      cancelled = true;
    };
  }, [siteKey, settle]);

  const getToken = useCallback((): Promise<string> => {
    const widgetId = widgetIdRef.current;
    if (!siteKey || widgetId === null || !window.grecaptcha) {
      return Promise.resolve("");
    }

    return new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => {
        pendingRef.current = null;
        try {
          window.grecaptcha?.reset(widgetId);
        } catch {
          // Widget already torn down — nothing to reset.
        }
        reject(new Error("reCAPTCHA timed out"));
      }, TOKEN_TIMEOUT_MS);

      pendingRef.current = { resolve, reject, timer };

      try {
        // Reset first so a second submission doesn't reuse a spent token.
        window.grecaptcha!.reset(widgetId);
        window.grecaptcha!.execute(widgetId);
      } catch (error) {
        pendingRef.current = null;
        clearTimeout(timer);
        reject(
          error instanceof Error
            ? error
            : new Error("reCAPTCHA could not be executed"),
        );
      }
    });
  }, [siteKey]);

  return { containerRef, ready, getToken };
}
